import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Spinner from "../../components/Spinner";
import { NeedsAttentionBand, TaskItem } from "./summary/NeedsAttentionBand";
import { RespiratoryStatusBand } from "./summary/RespiratoryStatusBand";
import { CareRecordBand } from "./summary/CareRecordBand";
import { CareJourneyRoadmap } from "../../components/CareJourneyRoadmap/CareJourneyRoadmap";
import { useClinicalStats } from "../../hooks/useClinicalStats";

import { createAppointmentApi } from "../../api-helpers/AppointmentApiHelper";
import {
  GridDataRequest as AppointmentGridDataRequest,
  GridDataResponse as AppointmentGridDataResponse,
} from "appointment-experience-client";
import {
  GridDataRequest as ClinicalFormsGridDataRequest,
  GridDataResponse as ClinicalFormsGridDataResponse,
} from "clinical-forms-experience-client";

import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { workflowStatesCache } from "../../grid/renderers/WorkflowStateCellValueGetter";
import { isAssessmentOverdue } from "../Assessment-v2/utils.assessment";
import { WorkflowConstants } from "../../helper/WorkflowConstants";
import { WorkflowState } from "common-util-experience-client";

import "../../assets/scss/custom/_patientSummaryRedesign.scss";

// Needs-attention's Assessments column is scoped to just these four module
// types for now (matches clinical_forms.name, same lookup
// ASSESSMENT_SCORE_DIRECTION in utils.assessment.ts uses).
const NEEDS_ATTENTION_ASSESSMENT_TYPES = new Set([
  "NPS",
  "COPD Assessment Test (CAT)",
  "Patient Activation Measure (PAM)",
  "mMRC Dyspnea Scale",
]);

interface WorkflowStateResponse {
  data: {
    stateName: string;
  };
}

interface AppointmentData {
  coachUserId: string;
  coachName: string;
  categoryName: string;
  webinarUrl: string;
  time: string | Date;
  durationMinutes: number;
  primaryCoachUserId: string;
  appointmentId: string;
  appointmentType: string;
  status: string;
  duration: number;
  appointmentMode: string;
  wfStateId: string;
  wfStateName: string;
}
interface AssessmentData {
  dateTime: string | number | Date;
  assessmentId: string;
  assessment: string;
  score: number | string;
  status: string; // You might need to rename AssementStatus to status
  lastCompletionDate: string | number | Date; // Add this property
  submittedAt: string | number | Date;
  previousScore?: number | string;
}
export const Summary = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const gridApi = createGridApi();
  const [loading, setLoading] = useState(false);

  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [assessment, setAssessment] = useState<AssessmentData[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [openTaskCount, setOpenTaskCount] = useState<number>(0);

  const [attendedCount, setAttendedCount] = useState<number>(0);
  const [missedCount, setMissedCount] = useState<number>(0);
  const appointmentApi = createAppointmentApi();
  const apiWorkflow = createWorkflowApi();
  const dispatch = useDispatch();

  const appointmentData = async (
    request: AppointmentGridDataRequest
  ): Promise<AppointmentGridDataResponse> => {
    const result = await appointmentApi.appointmentControllerGetGridData(
      request
    );
    return result.data;
  };

  const assessmentData = async (
    request: ClinicalFormsGridDataRequest
  ): Promise<ClinicalFormsGridDataResponse> => {
    const result = await gridApi.gridControllerGetAssessmentsGridData(request);
    return result.data;
  };
  const getAppointmentStatus = async (
    workflowId: string,
    stateId: string
  ): Promise<string> => {
    try {
      const response = (await apiWorkflow.workflowControllerGetWorkFlowState(
        workflowId,
        stateId
      )) as WorkflowStateResponse;
      return response.data.stateName;
    } catch (error) {
      console.error("Error fetching appointment status:", error);
      return "";
    }
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);

      const currentDate = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(currentDate.getDate() - 30);

      const request: AppointmentGridDataRequest = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "time",
            operator: "greater-than-equals",
            propertyValue: thirtyDaysAgo.toISOString(),
          },
        ],
        sortCriteria: [
          {
            propertyName: "time",
            descending: false,
          },
        ],
        startRow: 0,
        endRow: 300,
      };

      const response = await appointmentData(request);
      const fetchedAppointments = response.data || [];
      setAppointments(fetchedAppointments);

      // Fetch each unique (workflowId, stateId) pair once instead of once per appointment
      const uniqueStates = new Map<string, { workflowId: string; stateId: string }>();
      fetchedAppointments.forEach((appointment) => {
        if (!workflowStatesCache.has(appointment.wfStateId) && !uniqueStates.has(appointment.wfStateId)) {
          uniqueStates.set(appointment.wfStateId, {
            workflowId: appointment.wfDesignerId,
            stateId: appointment.wfStateId,
          });
        }
      });

      await Promise.all(
        Array.from(uniqueStates.values()).map(async ({ workflowId, stateId }) => {
          const status = await getAppointmentStatus(workflowId, stateId);
          if (status) {
            workflowStatesCache.set(stateId, status);
          }
        })
      );

      const statuses = fetchedAppointments.map((appointment) => ({
        ...appointment,
        status: workflowStatesCache.get(appointment.wfStateId) || "",
      }));

      let attended = 0;
      let missed = 0;

      // Count attended and missed appointments
      statuses.forEach((appointment) => {
        if (appointment.status === "Completed") {
          attended += 1;
        } else if (appointment.status === "No Show") {
          missed += 1;
        }
      });

      setAttendedCount(attended);
      setMissedCount(missed);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const request: ClinicalFormsGridDataRequest = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "status",
            operator: "equals",
            propertyValue: "submitted",
          },
        ],
        sortCriteria: [
          {
            propertyName: "submittedAt",
            descending: true,
          },
        ],
        startRow: 0,
        endRow: 300,
      };

      const response = await assessmentData(request);
      const fetchedAssessments = response.data || [];

      // Create a Map to track the latest assessment for each assessment type
      const latestAssessmentsMap = new Map();

      // Iterate through each fetched assessment (already sorted submittedAt
      // descending) - first occurrence per type is the latest submission,
      // second occurrence is the previous one, used for the trend arrow.
      fetchedAssessments.forEach((assessment) => {
        if (!latestAssessmentsMap.has(assessment.assessmentMasterId)) {
          latestAssessmentsMap.set(assessment.assessmentMasterId, {
            assessmentId: assessment.assessmentId,
            assessment: assessment.assessment,
            score: assessment.score,
            status: assessment.status,
            lastCompletionDate: assessment.submittedAt,
            previousScore: undefined,
          });
        } else {
          const existing = latestAssessmentsMap.get(
            assessment.assessmentMasterId
          );
          if (existing.previousScore === undefined) {
            existing.previousScore = assessment.score;
          }
        }
      });

      // Convert the Map values back to an array, most recently completed first
      // (the compact panel only shows the first few, so the most recent/
      // actionable assessments should be the ones that surface there).
      const latestAssessments = Array.from(latestAssessmentsMap.values()).sort(
        (a, b) =>
          new Date(b.lastCompletionDate).getTime() -
          new Date(a.lastCompletionDate).getTime()
      );
      // Set the latest assessments to state
      setAssessment(latestAssessments);
    } catch (error) {
      console.error("Error fetching assessment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const statesResponse =
        await apiWorkflow.workflowControllerGetStatesForWorkflow(
          WorkflowConstants.taskWorkflowType
        );
      const openStates = statesResponse.data.filter(
        (state: WorkflowState) => state.isEnd === false
      );

      const response = await gridApi.gridControllerGetTaskGridData({
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
          {
            propertyName: "wfStateId",
            operator: "in",
            propertyValue: openStates.map((state) => state.stateId),
          },
        ],
        sortCriteria: [{ propertyName: "dueAt", descending: false }],
        startRow: 0,
        endRow: 300,
      });

      const data = response.data?.data ?? [];
      setOpenTaskCount(response.data?.total ?? data.length);
      setTasks(data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  useEffect(() => {
    if (!patientId) return;
    void fetchAssessments();
    void fetchAppointments();
    void fetchTasks();
  }, [patientId]);

  useEffect(() => {
    dispatch(setPageTitle("Summary: Patient Profile | Spire360"));
  }, [dispatch]);

  const scopedAssessments = assessment.filter((item) =>
    NEEDS_ATTENTION_ASSESSMENT_TYPES.has(item.assessment)
  );

  const overdueModules = scopedAssessments.filter((item) =>
    isAssessmentOverdue(item.lastCompletionDate)
  );

  const { status: clinicalStatsStatus, stats: clinicalStats } = useClinicalStats(patientId);
  const vaccinations = clinicalStats?.vaccinations ?? [];

  return (
    <>
      {loading && <Spinner />}
      <div className="patient-summary-v2">
        <div className="patient-summary-v2__content">
          {/* Named slots so the wide layout can place the bands on a shared
              grid: Needs attention spans the Care journey and Respiratory
              status rows and ends level with Respiratory status. */}
          <div className="ps-dashboard-grid">
            {patientId && (
              <div className="ps-dashboard-slot ps-dashboard-slot--attention">
                <NeedsAttentionBand
                  patientId={patientId}
                  currentModules={overdueModules}
                  tasks={tasks}
                  openTaskCount={openTaskCount}
                  vaccinations={vaccinations}
                />
              </div>
            )}
            <div className="ps-dashboard-main">
              {patientId && (
                <div className="ps-dashboard-slot ps-dashboard-slot--journey">
                  <CareJourneyRoadmap patientId={patientId} heading="Care journey" />
                </div>
              )}
              {patientId && (
                <div className="ps-dashboard-slot ps-dashboard-slot--respiratory">
                  <RespiratoryStatusBand status={clinicalStatsStatus} stats={clinicalStats} />
                </div>
              )}
              {patientId && (
                <div className="ps-dashboard-slot ps-dashboard-slot--record">
                  <CareRecordBand
                    patientId={patientId}
                    attendedCount={attendedCount}
                    missedCount={missedCount}
                    appointments={appointments}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
